import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// ── Tokens kept in memory, lightly mirrored in localStorage ──────────────────
let accessToken: string | null = localStorage.getItem('hissab.access') || null;
let refreshToken: string | null = localStorage.getItem('hissab.refresh') || null;
let tenantSlug: string | null = localStorage.getItem('hissab.tenant') || null;

export const setTokens = (access: string | null, refresh?: string | null) => {
  accessToken = access;
  if (access) localStorage.setItem('hissab.access', access);
  else localStorage.removeItem('hissab.access');
  if (refresh !== undefined) {
    refreshToken = refresh;
    if (refresh) localStorage.setItem('hissab.refresh', refresh);
    else localStorage.removeItem('hissab.refresh');
  }
};

export const setTenant = (slug: string | null) => {
  tenantSlug = slug;
  if (slug) localStorage.setItem('hissab.tenant', slug);
  else localStorage.removeItem('hissab.tenant');
};

export const getTenant = () => tenantSlug;
export const getAccessToken = () => accessToken;

let onAuthLost: (() => void) | null = null;
export const registerAuthLost = (cb: () => void) => {
  onAuthLost = cb;
};

// ── Request interceptor: JWT + current tenant ────────────────────────────────
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (tenantSlug) {
    config.headers['X-Tenant-ID'] = tenantSlug;
  }
  return config;
});

// ── Response interceptor: transparent refresh on 401 ─────────────────────────
// Endpoints that must never be transparently retried (they either issue tokens
// themselves or are public). Everything else - including /auth/session,
// /auth/my-tenants and /auth/switch-tenant - gets a silent token refresh.
const NO_RETRY_ROUTES = [
  '/auth/login',
  '/auth/google',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || '';
    const noRetry =
      url.startsWith('/public/') || NO_RETRY_ROUTES.some((route) => url.startsWith(route));

    if (error.response?.status === 401 && !original._retry && !noRetry && refreshToken) {
      original._retry = true;
      try {
        const { data } = await api.post('/auth/refresh', { refreshToken });
        setTokens(data.accessToken, data.refreshToken ?? refreshToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        setTokens(null, null);
        if (onAuthLost) onAuthLost();
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
